// @ts-strict-ignore
import { AmountSummary } from 'types/approvals'
import { useAccountsContext } from './context/useAccountsContext'
import { useProfileContext } from './context/useProfileContext'
import { useAccountPreferencesQuery, useExchangeRateQuery } from './queries'
import { useApprovalsPreference } from './useApprovalsPreference'

const useApprovalConfigUtils = (amountSummary: AmountSummary) => {
  const { profile: { currencyCode } = {} } = useProfileContext()
  const { data: accountPreferences, isFetching: isFetchingAccountPreferences } =
    useAccountPreferencesQuery()
  const baseCurrency =
    (!isFetchingAccountPreferences && (accountPreferences?.currency || currencyCode)) || ''
  const { currentAccount } = useAccountsContext()
  const currentAccountReference = currentAccount?.reference
  const isApprovalQueryEnabled = !!currentAccountReference
  const { approvalFlowPreferences, isFetchingApprovalPreference } = useApprovalsPreference(
    currentAccountReference,
    isApprovalQueryEnabled
  )
  const isExchangeRateQueryEnabled = !!(
    baseCurrency !== amountSummary?.currency &&
    amountSummary?.amount &&
    amountSummary?.currency &&
    baseCurrency
  )
  const { data: exchangeRate } = useExchangeRateQuery({
    fromCurrency: amountSummary?.currency,
    toCurrency: baseCurrency,
    amount: amountSummary?.amount,
    enabled: isExchangeRateQueryEnabled,
  })

  return {
    approvalAmount: exchangeRate?.value || amountSummary?.amount,
    approvalFlowPreferences,
    isFetchingApprovalPreference,
  }
}

/**
 * Checks if Approvals is required for a payment flow or not
 * if no paymentFlow is passed, it will return all the flows with their corresponding boolean values
 *
 *
 * @param {string} paymentFlow - any of the supported flow types
 * @param {AmountSummary} amountSummary - option to check if approval is required for amount
 */
export const useApprovalsConfigForFlow = (
  paymentFlow?: string,
  amountSummary?: AmountSummary
): {
  enabledFlows?: string[]
  approvalsIsRequiredForFlow?: boolean
  isFetchingApprovalPreference?: boolean
  mayRequireApprovalForAmount?: boolean
} => {
  const { approvalAmount, approvalFlowPreferences, isFetchingApprovalPreference } =
    useApprovalConfigUtils(amountSummary)

  const enabledFlows = approvalFlowPreferences?.enabledFlows
  const isValidApprovalPreferenceFormat = Array.isArray(enabledFlows)
  if (!paymentFlow && isValidApprovalPreferenceFormat) {
    return { enabledFlows }
  }

  let approvalsIsRequiredForFlow =
    isValidApprovalPreferenceFormat && enabledFlows.includes(paymentFlow)
  const approvalBands = approvalFlowPreferences?.approvalBands
  if (
    amountSummary?.amount &&
    amountSummary?.currency &&
    approvalsIsRequiredForFlow &&
    approvalBands?.length
  ) {
    const amountValue = Number(approvalAmount)
    const approvalBandCheck = approvalBands?.find(band =>
      band?.to === undefined
        ? amountValue >= Number(band?.from)
        : amountValue >= Number(band?.from) && amountValue <= Number(band?.to)
    )
    approvalsIsRequiredForFlow = !!Number(approvalBandCheck?.approvals)
  }

  return { approvalsIsRequiredForFlow, isFetchingApprovalPreference }
}
